import { BarChartOutlined, BarsOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { Badge, Button, Dropdown, Flex, Modal, Space, Tabs, Tag, Typography, message } from 'antd';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useArchiveSurvey, useSurveys } from '../../api/Surveys';
import InviteButton from '../../components/InviteButton';
import InviteMemberModal from '../../components/InviteMemberModal';
import Loading from '../../components/Loading';
import SearchableTable from '../../components/SearchableTable';
import {
  SurveyListTabs,
  SurveyProcessingType,
  SurveyStatus,
  SurveyStatusText,
  UserRoleType,
} from '../../constants';
import useOrganizationType from '../../hooks/useOrganizationType';
import useRoles from '../../hooks/useRoles';
import useSuperuser from '../../hooks/useSuperuser';
import SurveyDetailsModal from './SurveyDetailsModal';

const { Title } = Typography;

const StyledDiv = styled.div`
  padding: 0 24px 24px;
`;

function MySurveys() {
  const { roles } = useRoles();
  const { isSuperuser } = useSuperuser();
  const { isLoading: organizationLoading, isHybridAccount, isMagicAccount } = useOrganizationType();
  const navigate = useNavigate();

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [activeTab, setActiveTab] = useState(SurveyListTabs.SURVEYS);

  const {
    data: surveys,
    isLoading: surveysLoading,
    isError: surveysError,
  } = useSurveys({
    select: (data) => {
      const sortedData = data.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      return {
        ...data,
        data: isSuperuser ? sortedData.filter((s) => !s.is_demo) : sortedData,
      };
    },
  });

  const { mutate: archiveSurvey, isLoading: archiveSurveyLoading } = useArchiveSurvey();

  const tabData = [
    {
      key: SurveyListTabs.SURVEYS,
      label: 'Projects',
    },
    {
      key: SurveyListTabs.ARCHIVE,
      label: SurveyListTabs.ARCHIVE,
    },
  ];

  const surveyRows = surveys?.data ?? [];

  const columns = [
    {
      title: `Projects (${
        surveyRows.filter((s) =>
          activeTab === SurveyListTabs.SURVEYS ? !s.is_archived : s.is_archived,
        ).length
      })`,
      dataIndex: 'name',
      render: (name) => <strong>{name}</strong>,
    },
    {
      title: 'Open in',
      key: 'navigation',
      render: (_, record) => {
        const isAdmin = roles.includes(UserRoleType.ADMINISTRATOR);
        const isEditor = roles.includes(UserRoleType.EDITOR);
        const isViewer = roles.includes(UserRoleType.VIEWER);
        const isClient = roles.includes(UserRoleType.CLIENT);
        const isPro = record.processing_type === SurveyProcessingType.PRO;
        const isMagic = record.processing_type === SurveyProcessingType.MAGIC;
        const isPublished = record.survey_status !== SurveyStatus.UNPUBLISHED;

        let showSurveyHub = false;
        let showDashboard = false;
        let enableSurveyHub = false;
        let enableDashboard = false;

        if (isSuperuser || isPro) {
          if (isAdmin || isEditor) {
            showSurveyHub = true;
            showDashboard = true;
            enableSurveyHub = true;
            enableDashboard = true;
          } else if (isViewer) {
            showDashboard = true;
            enableDashboard = true;
          } else if (isClient) {
            showDashboard = true;
            enableDashboard = isPublished;
          }
        } else if (isMagic) {
          if (isAdmin || isEditor) {
            showSurveyHub = true;
            showDashboard = true;
            if (isPublished) {
              enableSurveyHub = true;
              enableDashboard = true;
            }
          } else if (isViewer) {
            showDashboard = true;
            if (isPublished) {
              enableDashboard = true;
            }
          } else if (isClient) {
            showDashboard = true;
            if (isPublished) {
              enableDashboard = true;
            }
          }
        }

        return (
          <Space size="small">
            {showSurveyHub && (
              <Button
                type="default"
                size="small"
                icon={<BarsOutlined />}
                disabled={!enableSurveyHub}
                onClick={() => navigate(`/surveys/${JSON.stringify(record.id)}/hub`)}
                style={{
                  borderColor: enableSurveyHub ? '#1890ff' : undefined,
                  color: enableSurveyHub ? '#1890ff' : undefined,
                }}
              >
                Project Hub
              </Button>
            )}
            {showDashboard && (
              <Button
                type="primary"
                size="small"
                icon={<BarChartOutlined />}
                disabled={!enableDashboard}
                onClick={() => navigate(`/surveys/${JSON.stringify(record.id)}`)}
              >
                Dashboard
              </Button>
            )}
          </Space>
        );
      },
    },
    ...(isSuperuser
      ? [
          {
            title: 'Client',
            dataIndex: 'organization_name',
          },
        ]
      : []),
    ...(isHybridAccount || isSuperuser
      ? [
          {
            title: 'Coding mode',
            dataIndex: 'processing_type',
          },
        ]
      : []),
    {
      title: 'Status',
      dataIndex: 'survey_status',
      render: (status) => (
        <Space>
          <Badge color={status === SurveyStatus.UNPUBLISHED ? 'orange' : 'green'} />
          {SurveyStatusText[status]}
        </Space>
      ),
    },
    {
      key: 'action',
      width: 50,
      render: (_, record) => {
        const menuItems = [];
        if (roles.includes(UserRoleType.ADMINISTRATOR) || roles.includes(UserRoleType.EDITOR)) {
          menuItems.push({
            key: 'Archive',
            label: record.is_archived ? 'Unarchive project' : 'Archive project',
            onClick: () => {
              archiveSurvey(
                { surveyId: record.id, data: { is_archived: !record.is_archived } },
                {
                  onSuccess: () =>
                    message.success(record.is_archived ? 'Survey unarchived' : 'Survey archived'),
                },
              );
            },
          });
        } else {
          menuItems.push({
            key: 'Details',
            label: 'View details',
            onClick: () => setSelectedSurvey(record),
          });
        }

        return (
          <Dropdown menu={{ items: menuItems }}>
            <Button type="text">
              <MoreOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const tabBarOperations = (
    <>
      {roles.includes(UserRoleType.ADMINISTRATOR) && (
        <InviteButton onClick={() => setShowInviteModal(true)} />
      )}
      {(roles.includes(UserRoleType.ADMINISTRATOR) || roles.includes(UserRoleType.EDITOR)) && (
        <Dropdown
          menu={{
            items: [
              {
                key: 'survey',
                label: 'Survey',
                onClick: () => {
                  navigate('/surveys/upload');
                },
              },
              {
                key: 'transcript',
                label: (
                  <>
                    Interviews{' '}
                    <Tag color="blue" style={{ marginLeft: 4 }}>
                      Beta
                    </Tag>
                  </>
                ),
                onClick: () => {
                  if (isMagicAccount) {
                    Modal.info({
                      title: 'Contact us to enable interview analysis',
                      content: (
                        <Typography.Paragraph>
                          Please contact{' '}
                          <a href="mailto:support@fathomthat.ai">support@fathomthat.ai</a> to enable
                          interview uploads for your account.
                        </Typography.Paragraph>
                      ),
                      icon: null,
                    });
                  } else {
                    navigate('/transcripts/upload');
                  }
                },
              },
            ],
          }}
        >
          <Button icon={<PlusOutlined />}>New upload</Button>
        </Dropdown>
      )}
    </>
  );

  if (organizationLoading || surveysLoading) {
    return <Loading />;
  }

  if (surveysError) {
    return (
      <StyledDiv>
        <Title level={4}>My Projects</Title>
        <Typography.Text type="danger">
          We couldn&apos;t load projects right now. Please try refreshing the page.
        </Typography.Text>
      </StyledDiv>
    );
  }

  return (
    <StyledDiv>
      <Title level={4}>My Projects</Title>
      <Flex vertical gap={16}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabData}
          tabBarExtraContent={tabBarOperations}
        />
        <SearchableTable
          columns={columns}
          baseData={surveyRows.filter((s) =>
            activeTab === SurveyListTabs.SURVEYS ? !s.is_archived : s.is_archived,
          )}
          pagination={{
            defaultPageSize: 30,
            pageSizeOptions: [10, 20, 30, 50, 100],
            showTotal: (total) => `Total ${total} projects`,
          }}
          searchPlaceholder="Search projects..."
          rowKey="id"
          loading={archiveSurveyLoading}
          searchColumns={isSuperuser ? ['organization_name', 'name'] : ['name']}
        />
      </Flex>
      <InviteMemberModal closeModal={() => setShowInviteModal(false)} visible={showInviteModal} />
      <SurveyDetailsModal closeModal={() => setSelectedSurvey(null)} survey={selectedSurvey} />
    </StyledDiv>
  );
}

export default MySurveys;
